import { SocialMedia, LocalFacebookGroup } from '../services/projects';

// Basic address interface
export interface CompetitorAddress {
  formatted_address: string;
  location?: {
    type: 'Point';
    coordinates: [number, number]; // [longitude, latitude]
  };
  google_place_id?: string;
}

// Basic note interface
export interface CompetitorNote {
  note_id: string;
  content: string;
  created_by: {
    user_id: string;
    name: string;
  };
  created_at: string;
  updated_at?: string;
}

// Analysis interface
export interface CompetitorAnalysis {
  traffic_analysis?: {
    [key: string]: any;
  };
  customer_sentiment_analysis?: {
    [key: string]: any;
  };
  demographics_analysis?: {
    [key: string]: any;
  };
  competitor_score?: number;
  ai_recommendations?: string[];
}

// Base competitor interface with common properties
export interface BaseCompetitor {
  id: string;
  name: string;
}

// Simple competitor (used in CompetitionAnalysis)
export interface SimpleCompetitor extends BaseCompetitor {
  distance: number;
  rating: number;
  type: string;
}

// Full competitor (used in detailed views)
export interface FullCompetitor extends BaseCompetitor {
  project_id: string;
  address: CompetitorAddress;
  competition_type: 'Direct' | 'Indirect' | 'Potential';
  distance_miles?: number;
  threat_rating?: number;
  phone?: string;
  website?: string;
  rating?: number;
  business_types?: string[];
  business_status?: string;
  operating_hours?: {
    open_now: boolean;
    weekday_text: string[];
  };
  google_data?: {
    user_ratings_total: number;
    price_level?: number;
    photos: string[];
    place_id: string;
  };
  social_media?: SocialMedia;
  local_facebook_groups?: LocalFacebookGroup[];
  analysis?: CompetitorAnalysis;
  notes?: CompetitorNote[];
  metadata?: any;
  created_at: string;
  updated_at?: string;
}

// Convert SimpleCompetitor to FullCompetitor
export function convertToFullCompetitor(
  simpleCompetitor: SimpleCompetitor, 
  projectId: string
): FullCompetitor {
  return {
    id: simpleCompetitor.id,
    project_id: projectId,
    name: simpleCompetitor.name,
    address: { formatted_address: '' }, // Default empty address
    competition_type: simpleCompetitor.type as 'Direct' | 'Indirect' | 'Potential',
    distance_miles: simpleCompetitor.distance,
    threat_rating: simpleCompetitor.rating,
    created_at: new Date().toISOString()
  };
}

// Competition analysis interface
export interface CompetitionAnalysis {
  projectId: string;
  competitors: SimpleCompetitor[];
}

// Data for creating a competitor
export interface CreateCompetitorData {
  project_id: string;
  name: string;
  address: CompetitorAddress;
  competition_type: 'Direct' | 'Indirect' | 'Potential';
  distance_miles?: number;
  threat_rating?: number;
  phone?: string;
  website?: string;
  rating?: number;
  business_types?: string[];
  business_status?: string;
  operating_hours?: {
    open_now: boolean;
    weekday_text: string[];
  };
  google_data?: {
    user_ratings_total: number;
    price_level?: number;
    photos: string[];
    place_id: string;
  };
  social_media?: SocialMedia;
  local_facebook_groups?: LocalFacebookGroup[];
  analysis?: CompetitorAnalysis;
  metadata?: any;
}

// Data for updating a competitor
export interface UpdateCompetitorData {
  name?: string;
  address?: CompetitorAddress;
  competition_type?: 'Direct' | 'Indirect' | 'Potential';
  distance_miles?: number;
  threat_rating?: number;
  social_media?: SocialMedia;
  local_facebook_groups?: LocalFacebookGroup[];
  analysis?: CompetitorAnalysis;
  metadata?: any;
}

// Query params for competitors
export interface CompetitorQuery {
  project_id?: string;
  name?: string;
  competition_type?: 'Direct' | 'Indirect' | 'Potential';
  threat_rating_min?: number;
  threat_rating_max?: number;
  distance_max?: number;
  skip?: number;
  limit?: number;
}

// Data for creating a note
export interface CreateCompetitorNoteData {
  content: string;
}
